import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { LogOut, MailCheck, Loader2, Send } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import LanguageSwitcher from "./LanguageSwitcher";

/**
 * Full-screen gate shown to logged-in users whose email is not yet
 * confirmed. Blocks ALL app UI until they click the link in the email
 * OR the admin manually flips `email_verified=true`. Comes BEFORE the
 * "in attesa di associazione" gate so unverified users never see any
 * tenant data.
 */
export default function EmailVerificationGate() {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const [sending, setSending] = useState(false);
  const [msg, setMsg] = useState(null);
  const [err, setErr] = useState(null);

  const resend = async () => {
    if (!user?.email) return;
    setSending(true);
    setMsg(null);
    setErr(null);
    try {
      await api.post("/auth/resend-verification", { email: user.email });
      setMsg(t("verify_email.resend_sent"));
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      data-testid="email-verification-gate"
      className="min-h-screen bg-white flex flex-col"
    >
      <div className="flex justify-between items-center p-5 md:p-6">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-[#0A0A0B] flex items-center justify-center">
            <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none">
              <path d="M4 20 L12 4 L20 20 L17 20 L15.4 16 L8.6 16 L7 20 Z" fill="white" />
              <path d="M9.7 13 L12 8 L14.3 13 Z" fill="#2563EB" />
            </svg>
          </div>
          <div className="font-heading font-bold text-lg tracking-tight text-slate-900">AUTEXIO</div>
        </div>
        <LanguageSwitcher />
      </div>

      <div className="flex-1 flex items-center justify-center px-6 pb-16">
        <div className="w-full max-w-md text-center">
          <div className="w-16 h-16 mx-auto rounded-full bg-blue-50 border border-blue-100 flex items-center justify-center">
            <MailCheck className="w-8 h-8 text-blue-600" />
          </div>

          <p className="mt-6 text-[11px] uppercase tracking-[0.24em] text-blue-600 font-semibold">
            {t("email_gate.overline")}
          </p>
          <h1 className="mt-2 font-heading font-bold text-3xl md:text-4xl tracking-tighter text-slate-900">
            {t("email_gate.title")}
          </h1>
          <p className="mt-4 text-sm text-slate-600 leading-relaxed">
            {t("email_gate.body", { email: user?.email || "" })}
          </p>
          <p className="mt-2 text-xs text-slate-400">
            {t("email_gate.spam_hint")}
          </p>

          {msg && (
            <div
              data-testid="email-gate-resend-success"
              className="mt-6 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3"
            >
              {msg}
            </div>
          )}
          {err && (
            <div
              data-testid="email-gate-resend-error"
              className="mt-6 text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl px-4 py-3"
            >
              {err}
            </div>
          )}

          <div className="mt-8 flex flex-col gap-3">
            <Button
              onClick={resend}
              disabled={sending}
              data-testid="email-gate-resend-btn"
              className="w-full h-11 rounded-full bg-blue-600 hover:bg-blue-700 text-white font-semibold"
            >
              {sending
                ? <Loader2 className="w-4 h-4 animate-spin" />
                : (<><Send className="w-4 h-4 mr-2" /> {t("email_gate.resend_button")}</>)}
            </Button>
            <Button
              onClick={logout}
              variant="outline"
              data-testid="email-gate-logout-btn"
              className="w-full h-11 rounded-full border-slate-200 text-slate-600 hover:bg-slate-50"
            >
              <LogOut className="w-4 h-4 mr-2" /> {t("email_gate.logout")}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
